<script setup>

</script>

<template>
  <div class="outreach">

    <!-- HERO -->
    <section class="hero">
      <div class="container py-5">
        <div class="row justify-content-center text-center">
          <div class="col-lg-10">
            <h1 class="hero-title">
              Affordable, high-quality development for small businesses
              <span class="hero-subline">No hiring. No expensive consultants. <br/>Just results.</span>
            </h1>
            <p class="hero-subtitle mt-3">
              We’re your plug-and-play dev team, ready to build your app, website, or automation tools — with clear pricing and fast turnaround.
            </p>

            <ul class="hero-points list-unstyled d-flex flex-wrap justify-content-center gap-3 mt-4">
              <li class="point">Led by senior devs, supported by our training school grads</li>
              <li class="point">Ideal for businesses without an in-house team</li>
              <li class="point">Flexible packages starting at $800 monthly</li>
            </ul>

            <div class="btn-pair mt-4">
              <RouterLink :to="{ name: 'contact' }" class="btn btn-primary btn-lg">
                Book Your Free Consult
              </RouterLink>
              <RouterLink :to="{ name: 'services' }" class="btn btn-outline-primary btn-lg">
                See Packages & Pricing
              </RouterLink>
            </div>
          </div>
        </div>
      </div>
      <div class="hero-wave"></div>
    </section>

    <!-- QUICK VALUE / WHY US -->
    <section class="value">
      <div class="container py-5">
        <div class="row g-4">
          <div class="col-md-4">
            <div class="v-card shadow-sm rounded-4 p-4 h-100">
              <div class="v-emoji">⚡</div>
              <h3 class="v-title">Fast, predictable delivery</h3>
              <p class="v-text mb-0">
                Senior-led builds with clear scopes and timelines—no surprises.
              </p>
            </div>
          </div>
          <div class="col-md-4">
            <div class="v-card shadow-sm rounded-4 p-4 h-100">
              <div class="v-emoji">💙</div>
              <h3 class="v-title">Built for small business</h3>
              <p class="v-text mb-0">
                Practical, right-sized solutions that move the needle—without overhead.
              </p>
            </div>
          </div>
          <div class="col-md-4">
            <div class="v-card shadow-sm rounded-4 p-4 h-100">
              <div class="v-emoji">💡</div>
              <h3 class="v-title">Flexible packages</h3>
              <p class="v-text mb-0">
                Start small, scale when you’re ready. Packages from <strong>$800</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="packages">
      <div class="container py-5">
        <div class="text-center mb-4">
          <div class="eyebrow text-uppercase fw-semibold">Popular Options</div>
          <h2 class="section-title mt-2">Pick a pace that fits</h2>
        </div>

        <div class="row g-4">
          <div class="col-md-6 col-lg-4">
            <div class="p-card shadow-sm rounded-4 p-4 h-100 d-flex flex-column">
              <div class="p-badge">Outsourced</div>
              <h3 class="p-price mt-2">$800 - $4,200/mo</h3>
              <p class="p-text">10 to 60 dev hours per month.</p>
              <ul class="p-list mb-0">
                <li>Rollover unused hours month to month</li>
                <li>Lowest monthly cost</li>
              </ul>
              <div class="mt-auto pt-3">
                <RouterLink :to="{ name: 'contact' }" class="btn btn-primary w-100">Book a Consult</RouterLink>
              </div>
            </div>
          </div>

          <div class="col-md-6 col-lg-4">
            <div class="p-card shadow-sm rounded-4 p-4 h-100 d-flex flex-column">
              <div class="p-badge best">Staff Augmentation</div>
              <h3 class="p-price mt-2">$60 - $70/hour</h3>
              <p class="p-text">Dedicated part time or full time developers.</p>
              <ul class="p-list mb-0">
                <li>Integrates with your existing staff or projects</li>
                <li>Balance your monthly and hourly costs</li>
              </ul>
              <div class="mt-auto pt-3">
                <RouterLink :to="{ name: 'contact' }" class="btn btn-primary w-100">Book a Consult</RouterLink>
              </div>
            </div>
          </div>

          <div class="col-md-12 col-lg-4">
            <div class="p-card shadow-sm rounded-4 p-4 h-100 d-flex flex-column">
              <div class="p-badge alt">Project-Based</div>
              <h3 class="p-price mt-2">Custom</h3>
              <p class="p-text">Your scope, your call with milestones and dedicated delivery.</p>
              <ul class="p-list mb-0">
                <li>Migrations, redesigns, integrations, new builds, and AI cleanup</li>
                <li>Lowest hourly cost</li>
              </ul>
              <div class="mt-auto pt-3">
                <RouterLink :to="{ name: 'contact' }" class="btn btn-primary w-100">Start Your Project</RouterLink>
              </div>
            </div>
          </div>
        </div>

        <p class="mini-note text-center mt-3 mb-0">
          Want a deeper breakdown? <RouterLink :to="{ name: 'services' }">See full Services</RouterLink>
        </p>
      </div>
    </section>

    <!-- SOCIAL PROOF (SHORT) -->
    <section class="proof">
      <div class="container py-5">
        <div class="proof-card shadow-sm rounded-4 p-4 p-md-5 text-center">
          <h3 class="proof-title mb-2">Senior-led, student-powered</h3>
          <p class="proof-text mb-0">
            Our senior engineers lead every project, with delivery support from graduates of our own
            training school—so you get enterprise-grade quality at small-business prices.
          </p>
        </div>
      </div>
    </section>

    <!-- FINAL CTA -->
    <section class="cta">
      <div class="container py-5">
        <div class="cta-card shadow-sm rounded-4 p-5 text-center">
          <h2 class="cta-title mb-3">Let’s build something your customers love.</h2>
          <p class="cta-text mb-4">Book a quick call and we’ll walk through scope, timeline, and budget—no pressure.</p>
          <div class="btn-pair">
            <RouterLink :to="{ name: 'contact' }" class="btn btn-light btn-lg cta-btn">
              Book Your Free Consult
            </RouterLink>
            <RouterLink :to="{ name: 'services' }" class="btn btn-outline-light btn-lg cta-btn">
              See Packages
            </RouterLink>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<style scoped>
/* Shared button pair (matches site) */
.btn-pair{display:flex;flex-wrap:wrap;justify-content:center;gap:.75rem}
.btn-pair .btn{flex:1 1 100%;max-width:320px}
@media (min-width:768px){.btn-pair .btn{flex:0 0 auto}}

/* HERO */
.hero{background:#f7f8fc;border-bottom:1px solid #edf1f6;position:relative;overflow:hidden}
.hero-title{color:#055499;font-weight:800;font-size:clamp(2rem,1.4rem + 2.2vw,3rem);line-height:1.2;margin:0}
.hero-subline{display:block;font-weight:700;color:#0f172a;opacity:.9;margin-top:.35rem}
.hero-subtitle{color:#2b2b2b;font-size:clamp(1.05rem,.95rem + .6vw,1.25rem);line-height:1.6;max-width:52rem;margin:0 auto}
.hero-points .point{background:#fff;border:1px solid #e5e7eb;border-radius:999px;padding:.45rem .9rem;font-weight:600;color:#0f172a;box-shadow:0 1px 0 rgba(16,24,40,.04)}
.hero-wave{position:absolute;left:0;right:0;bottom:-1px;height:32px;background:linear-gradient(90deg,rgba(5,84,153,.12),rgba(5,84,153,0));}

/* VALUE */
.value{background:#fff;border-top:1px solid #edf1f6}
.v-card{background:#f7f8fc}
.v-emoji{font-size:1.5rem;line-height:1}
.v-title{font-weight:700;margin:.25rem 0}
.v-text{color:#2b2b2b;margin:0}

/* PACKAGES TEASER */
.packages{background:#fff;border-top:1px solid #edf1f6}
.eyebrow{color:#055499;letter-spacing:.08em;font-size:1.25rem}
.section-title{color:#111;font-weight:700;font-size:clamp(1.6rem,1.2rem + 1.2vw,2.25rem);margin:0}
.p-card{background:#f7f8fc}
.p-badge{display:inline-block;background:#e9f2fb;color:#055499;font-weight:700;border-radius:8px;padding:.25rem .6rem}
.p-badge.best{background:#dfeffc}
.p-badge.alt{background:#eef4ff}
.p-price{color:#111;font-weight:700;margin:0}
.p-text{color:#2b2b2b;margin:.25rem 0 0}
.p-list{margin:.5rem 0 0;padding-left:1.1rem}
.mini-note{color:#6b7280}

/* PROOF */
.proof{background:#fff;border-top:1px solid #edf1f6}
.proof-card{background:linear-gradient(180deg,#ffffff 0%,#f7f8fc 100%)}
.proof-title{font-weight:700;margin:0}
.proof-text{color:#2b2b2b;margin:0}

/* CTA BAND */
.cta{background:#fff;border-top:1px solid #edf1f6}
.cta-card{background:linear-gradient(135deg,#055499 0%,#0b6fce 100%);color:#fff}
.cta-title{font-weight:800;font-size:clamp(1.6rem,1.2rem + 1.2vw,2.25rem);margin:0}
.cta-text{font-size:1.1rem;line-height:1.6;max-width:48rem;margin:0 auto;opacity:.96}
.cta-btn{font-weight:700;border-radius:999px;padding-inline:1.4rem;box-shadow:0 6px 18px rgba(0,0,0,.12)}
</style>
